import siteFixtures from '../../fixtures/sites.json';
import {
  contactName,
  contactPurposes,
  countryCode,
  defaultLanguageForCountry,
  resolveContacts,
  scopeLevelAt,
  scopeLabel,
  siteCountryRegion,
  type Contact,
  type ContactInput,
  type ContactPatch,
  type ContactPurpose,
  type ContactResolution,
  type ContactScope,
} from '../domain/contact';
import type { Site } from '../domain/inventory';
import type { ContactsApi } from './contacts-api';
import { ConflictError, NotFoundError, ValidationError } from './errors';
import { withoutUndefined } from './patch';

const wait = async (milliseconds = 60): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const clone = <T>(value: T): T => structuredClone(value);

interface FixtureSite {
  id: string;
  customerId: string;
  name: string;
  displayName?: string;
  labels?: Record<string, string>;
  contactName?: string;
  contactPhone?: string;
}

const sites = new Map(
  (siteFixtures as FixtureSite[]).map((site): [string, Site] => [site.id, { ...site, version: 1 }]),
);

function splitName(name: string): { firstName: string; lastName?: string } {
  const parts = name.trim().split(/\s+/);
  const lastName = parts.length > 1 ? parts.pop() : undefined;
  return { firstName: parts.join(' '), ...(lastName ? { lastName } : {}) };
}

/**
 * Seeds one site contact per fixture site that has a contact name, the way
 * the migration turns sites' contact fields into contacts.
 */
function seedContacts(): Contact[] {
  return (siteFixtures as FixtureSite[])
    .filter((site) => site.contactName?.trim())
    .map((site, index) => ({
      id: `contact-seed-${index + 1}`,
      customerId: site.customerId,
      ...splitName(site.contactName ?? ''),
      ...(site.contactPhone ? { phone: site.contactPhone } : {}),
      ...(defaultLanguageForCountry(site.labels?.country)
        ? { language: defaultLanguageForCountry(site.labels?.country) }
        : {}),
      scopes: [
        { level: 'SITE', siteId: site.id, purposes: ['OPERATIONS', 'RECEIVING'], primary: true },
      ],
      version: 0,
    }));
}

/**
 * In-memory Contacts for tests and the default (mock) mode, with the
 * backend's rules: a site scope must be the customer's, one primary per
 * scope and purpose, and a version check on update.
 */
export class MockContactsApi implements ContactsApi {
  private contacts: Contact[] = seedContacts();
  private counter = 0;

  async listCustomerContacts(customerId: string, includeInactive = false): Promise<Contact[]> {
    await wait();
    return clone(
      this.contacts.filter(
        (contact) => contact.customerId === customerId && (includeInactive || !contact.inactive),
      ),
    );
  }

  async listSiteContacts(siteId: string): Promise<Contact[]> {
    await wait();
    const site = sites.get(siteId);
    if (!site) throw new NotFoundError(`site ${siteId} not found`);
    return clone(
      this.contacts.filter(
        (contact) =>
          contact.customerId === site.customerId &&
          !contact.inactive &&
          contact.scopes.some((scope) => scopeLevelAt(scope, site) !== undefined),
      ),
    );
  }

  async createContact(input: ContactInput): Promise<Contact> {
    await wait(100);
    const contact: Contact = {
      ...withoutUndefined(input),
      scopes: input.scopes ?? [],
      id: `contact-mock-${Date.now()}-${(this.counter += 1)}`,
      version: 0,
    };
    if (!contact.language) {
      const first = contact.scopes[0];
      const country =
        first?.level === 'SITE' && first.siteId
          ? siteCountryRegion(sites.get(first.siteId) ?? ({} as Site)).country
          : first?.country;
      const language = defaultLanguageForCountry(country);
      if (language) contact.language = language;
    }
    this.check(contact);
    this.contacts.push(contact);
    return clone(contact);
  }

  async updateContact(patch: ContactPatch): Promise<Contact> {
    await wait(100);
    const index = this.contacts.findIndex((contact) => contact.id === patch.id);
    const existing = this.contacts[index];
    if (!existing) throw new NotFoundError(`contact ${patch.id} not found`);
    if (existing.version !== patch.version) {
      throw new ConflictError(
        'This contact was changed by someone else; reload and try again',
        existing.version,
      );
    }
    const changes: Partial<ContactPatch> = { ...patch };
    const clear = changes.clear;
    delete changes.id;
    delete changes.version;
    delete changes.clear;
    const next: Contact = { ...existing, ...withoutUndefined(changes) };
    for (const field of clear ?? []) {
      if (field === 'scopes') next.scopes = [];
      else delete next[field];
    }
    for (const field of ['firstName', 'lastName', 'userId'] as const) {
      if (next[field] === '') delete next[field];
    }
    next.version = existing.version + 1;
    this.check(next);
    this.contacts[index] = next;
    return clone(next);
  }

  async deleteContact(id: string): Promise<void> {
    await wait();
    if (!this.contacts.some((contact) => contact.id === id)) {
      throw new NotFoundError(`contact ${id} not found`);
    }
    this.contacts = this.contacts.filter((contact) => contact.id !== id);
  }

  async resolveContacts(siteId: string, purpose?: ContactPurpose): Promise<ContactResolution[]> {
    await wait();
    const site = sites.get(siteId);
    if (!site) throw new NotFoundError(`site ${siteId} not found`);
    const purposes = purpose ? [purpose] : [...contactPurposes];
    return clone(purposes.map((p) => resolveContacts(this.contacts, site, p)));
  }

  private check(contact: Contact): void {
    if (!contactName(contact)) throw new ValidationError('a contact needs a first or last name');
    for (const scope of contact.scopes) {
      if (scope.level === 'SITE') {
        const site = scope.siteId ? sites.get(scope.siteId) : undefined;
        if (!site || site.customerId !== contact.customerId) {
          throw new ValidationError(
            `site ${scope.siteId ?? ''} isn't a site of this contact's customer`,
          );
        }
      }
      if (scope.level === 'REGION') {
        // Stored as an ISO 3166-1 alpha-2 code, like leatherback.
        const code = countryCode(scope.country);
        if (!code) {
          throw new ValidationError(
            `country must be an ISO 3166-1 alpha-2 code, e.g. NO, not "${scope.country ?? ''}"`,
          );
        }
        scope.country = code;
      }
    }
    if (contact.inactive) return;
    for (const scope of contact.scopes.filter((candidate) => candidate.primary)) {
      for (const other of this.contacts) {
        if (other.id === contact.id || other.inactive || other.customerId !== contact.customerId)
          continue;
        const taken = other.scopes.find(
          (candidate) =>
            candidate.primary &&
            sameScope(candidate, scope) &&
            candidate.purposes.some((p) => scope.purposes.includes(p)),
        );
        if (taken) {
          const purposeName = taken.purposes.find((p) => scope.purposes.includes(p)) ?? '';
          throw new ValidationError(
            `${contactName(other)} is already the primary ${purposeName.toLowerCase().replace('_', ' ')} contact for ${scopeLabel(scope, sites).replace(/^Whole customer$/, 'the customer')}`,
          );
        }
      }
    }
  }
}

function sameScope(a: ContactScope, b: ContactScope): boolean {
  if (a.level !== b.level) return false;
  if (a.level === 'SITE') return a.siteId === b.siteId;
  if (a.level === 'REGION') {
    return (
      countryCode(a.country) === countryCode(b.country) &&
      (a.region ?? '').toLowerCase() === (b.region ?? '').toLowerCase()
    );
  }
  return true;
}

export const contactsApi = new MockContactsApi();
